import React, { useEffect, useState } from "react";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import { FaChalkboardTeacher } from "react-icons/fa";

import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import UserExistenceOverview from "../components/UserExistenceOverview";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import OrientationPrompt from "../components/OrientationPrompt";

import {
  whoami,
  getClasses,
  getTeachersBySchoolId,
  getSchools,
  getCountryGrades,
} from "../services/APIClient";
import { isTeacher } from "../utils/auth";

import "../components/ClassCard.css";

function UserAdmin() {
  const [schoolid, setSchoolid] = useState("");
  const [schoolName, setSchoolName] = useState("Loading school information...");
  const [rollNumber, setRollNumber] = useState("");
  const [teacherid, setTeacherid] = useState(null); 
  const [name, setName] = useState("");
  const [teachers, setTeachers] = useState(null);
  const [teacherEmail, setTeacherEmail] = useState(null); 
  const [classes, setClasses] = useState([]); 
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]); 
  const [countryId, setCountryId] = useState([]);
  
  const [isLoadingSchool, setIsLoadingSchool] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Check if current user is a teacher
  const teacherMode = isTeacher();

  // Check if school has been found
  const isSchoolFound = schoolid && schoolid !== "" && schoolName !== "Loading school information..." && schoolName !== "Please enter school roll number and press 'Find School'";

  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  // ✅ Fetch user and school information based on role
  useEffect(() => {
    const fetchUserAndSchoolInfo = async () => {
      try {
        const response = await whoami();
        const stakeholder = response.data.stakeholder;
        setTeacherid(stakeholder.id);
        setName(stakeholder.name);
        setTeacherEmail(response.data.email);

        // ✅ TEACHER MODE: Auto-load their school
        if (teacherMode) {
          const teacherSchoolId = stakeholder.schoolid || stakeholder.schoolId;
          
          if (!teacherSchoolId) {
            console.error("❌ Teacher has no school ID!");
            setSchoolName("Error: No school assigned");
            setLoadError("No school assigned to your account");
            setIsLoadingSchool(false);
            return;
          }

          console.log("🏫 Teacher mode: Auto-loading school ID:", teacherSchoolId);
          setSchoolid(teacherSchoolId);

          // Fetch school details
          try {
            const response = await getSchools();
            const schools = response.data || response;
            const teacherSchool = Array.isArray(schools) 
              ? schools.find(s => s.id === teacherSchoolId)
              : null;
            
            if (teacherSchool) {
              setSchoolName(teacherSchool.name);
              setRollNumber(teacherSchool.rollnumber || "");
              console.log("✅ Loaded teacher's school:", teacherSchool.name);
            } else {
              setSchoolName("School not found");
              setLoadError("School not found in database");
            }
          } catch (error) {
            console.error("❌ Error fetching school details:", error);
            setSchoolName("Error loading school");
            setLoadError(`Error loading school: ${error.message}`);
          }

          setIsLoadingSchool(false);
        } else {
          // ✅ ALPACA-STAFF MODE: Show search interface
          setSchoolName("Please enter school roll number and press 'Find School'");
          setIsLoadingSchool(false);
        }
      } catch (error) {
        console.error("❌ Error in whoami:", error);
        setSchoolName("Error loading user information");
        setLoadError(`Error loading user: ${error.message}`);
        setIsLoadingSchool(false);
      }
    };

    fetchUserAndSchoolInfo();
  }, [teacherMode]);

  // Fetch teachers, classes, and school data when school ID changes
  useEffect(() => {
    async function fetchSchoolData() {
      if (!schoolid || !isSchoolFound) {
        console.log("No valid schoolid yet.");
        return;
      }

      try {
        setLoading(true);
        console.log("Fetching school data for school ID:", schoolid);

        // Fetch teachers for the school
        const teachersResponse = await getTeachersBySchoolId(schoolid);
        console.log("Teachers response for school ID:", schoolid, teachersResponse);
        setTeachers(teachersResponse);

        // Fetch classes
        const classesResponse = await getClasses();
        console.log("Classes response:", classesResponse);
        classesResponse.sort(
          (a, b) => new Date(b.date_created) - new Date(a.date_created)
        );
        setClasses(classesResponse);

        // Fetch school details if not already loaded (for staff mode)
        if (!teacherMode) {
          const schoolsResponse = await getSchools();
          console.log("Schools response:", schoolsResponse);
          const matchingSchool = schoolsResponse.data.find(
            (school) => school.id === schoolid
          );

          if (matchingSchool) {
            console.log("Matching school found:", matchingSchool);
            const rollNum = matchingSchool.rollnumber;
            const countryid = matchingSchool.countryid;
            setCountryId(countryid);
            setRollNumber(rollNum);

            // Fetch country-specific grades
            getCountryGrades(countryid)
              .then((gradesResponse) => {
                console.log("Country grades response:", gradesResponse);
                setGradeLevelOptions([gradesResponse]);
              })
              .catch((error) => {
                console.error("Error fetching country grades:", error);
              });
          } else {
            console.log("No matching school found with school ID:", schoolid);
          }
        } else {
          // For teacher mode, we already have school info, just get country grades
          const schoolsResponse = await getSchools();
          const matchingSchool = schoolsResponse.data.find(
            (school) => school.id === schoolid
          );
          
          if (matchingSchool) {
            const countryid = matchingSchool.countryid;
            setCountryId(countryid);

            getCountryGrades(countryid)
              .then((gradesResponse) => {
                console.log("Country grades response:", gradesResponse);
                setGradeLevelOptions([gradesResponse]);
              })
              .catch((error) => {
                console.error("Error fetching country grades:", error);
              });
          }
        }
      } catch (error) {
        console.error("Error fetching school data:", error);
        setLoadError(`Error loading school data: ${error.message}`);
      } finally {
        setLoading(false);
        console.log("Data fetching complete.");
      }
    }

    if (isSchoolFound) {
      console.log("Valid schoolid found, fetching data...");
      fetchSchoolData();
    }
  }, [schoolid, isSchoolFound, teacherMode]);

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={`Welcome ${name}!`}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

          {/* Page Header */}
          <div className="w-75 mt-4 mb-3 mx-auto">
            <h2 className="text-white">User Administration</h2>
            <p className="text-white">
              {teacherMode 
                ? "View and manage users in your school"
                : "View and manage users across schools"
              }
            </p>
          </div>

          {/* ✅ ROLE INDICATOR */}
          {/* {teacherMode && (
            <div className="w-75 mx-auto mb-4">
              <Alert variant="info">
                <strong>👨‍🏫 School Administrator Mode</strong>
                <p className="mb-0">You are viewing user information for your school only.</p>
              </Alert>
            </div>
          )} */}

          {/* ✅ ERROR DISPLAY */}
          {loadError && (
            <div className="w-75 mx-auto mb-4">
              <Alert variant="danger">
                <strong>⚠️ Error Loading School</strong>
                <p className="mb-0">{loadError}</p>
              </Alert>
            </div>
          )}

          {/* ✅ SCHOOL SELECTOR OR DISPLAY */}
          <div className="w-75 mx-auto mb-4">
            {teacherMode ? (
              // ✅ TEACHER VIEW: Show their school (locked)
              <div className="bg-white rounded shadow-sm p-4">
                <h4 className="mb-3">Your School</h4>
                <Alert variant="secondary" className="mb-3">
                  <FaChalkboardTeacher className="me-2" />
                  You are viewing user information for the following school:
                </Alert>
                {isLoadingSchool ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading your school information...</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-primary">{schoolName}</h3>
                    {rollNumber && (
                      <p className="text-muted">Roll Number: {rollNumber}</p>
                    )}
                  </>
                )}
              </div>
            ) : (
              // ✅ ALPACA-STAFF VIEW: Show search interface
              <div className="bg-white rounded shadow-sm p-4">
                <h4 className="mb-3">School Selection</h4>
                <p className="text-muted mb-3">Select a school to view user information</p>
                <SchoolRollNumberInput
                  onSchoolIdRetrieved={setSchoolid}
                  schoolNameRetrieved={(name, rollNum) => {
                    setSchoolName(name);
                    if (rollNum) setRollNumber(rollNum);
                  }}
                />
                {schoolName && schoolName !== "Please enter school roll number and press 'Find School'" && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <div>
                      <strong className="me-2">Selected School:</strong>
                      <span className="text-primary">{schoolName}</span>
                    </div>
                    {rollNumber && (
                      <div className="text-muted small mt-1">
                        Roll Number: {rollNumber}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Overview Section */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="light" size="lg" />
              <p className="text-white mt-3">Loading user data...</p>
            </div>
          ) : isSchoolFound ? (
            <div className="row-position">
              <UserExistenceOverview
                teachers={teachers}
                schoolid={schoolid}
                schoolName={schoolName}
                name={name}
                rollNumber={rollNumber}
                teacherEmail={teacherEmail}
                countryId={countryId}
              />
            </div>
          ) : !isLoadingSchool && (
            <div className="text-center py-5">
              <h4 className="text-white">
                {teacherMode 
                  ? loadError || "Unable to load school information. Please contact support."
                  : "Please select a school to view user information."
                }
              </h4>
            </div>
          )}

        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default UserAdmin;